import { useMutation, useQueryClient } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

import { markWebsiteAsChanged } from '../../../../../../hooks/usePlusWebsites';
import type { WebsiteSectionDetails } from './useGetWebsiteSectionDetails';
import { websiteSectionDetailsQueryKey } from './useGetWebsiteSectionDetails';

interface UpdateWebsiteSectionParams {
  websiteId: string;
  sectionId: string;
  data: Record<string, unknown>;
}

function updateWebsiteSection({ websiteId, sectionId, data }: UpdateWebsiteSectionParams) {
  return axiosPrivate
    .patch(`/v3/pharmacist/plus-websites/${websiteId}/sections/${sectionId}`, { data })
    .then((r) => r.data);
}

export function useUpdateWebsiteSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateWebsiteSection,
    // Write the saved data straight into the section-details cache. The edit form seeds from
    // this cache on open (RJSF's uncontrolled `initialFormData`), so without this a reopened
    // header or footer would show the pre-save data until a full page refresh cleared the cache.
    onSuccess: (_data, { websiteId, sectionId, data }) => {
      queryClient.setQueryData<WebsiteSectionDetails>(
        websiteSectionDetailsQueryKey(websiteId, sectionId),
        (previous) => (previous ? { ...previous, data } : previous)
      );
      markWebsiteAsChanged(queryClient, websiteId);
    },
    meta: {
      successMessage: 'Section updated successfully',
      errorMessage: 'Failed to update section',
    },
  });
}
